
import Promise from "plugins/bluebird"
import $ from "jquery"
import dayjs from "dayjs"
import "dayjs/locale/zh-cn"
import "dayjs/locale/zh-tw"

import DataRetriever from "app/data_retriever"
import {Page} from "app/multi_device"


import I18n from "plugins/i18n-js"


export interface TransactionCountsGroupedByMonth {
  first_day_of_the_month: string,
  transaction_count: number,
}


const create = (
  element: string | JQuery,
  {
    transaction_counts_grouped_by_months,
    transactions_price_spacious,
    currency_unit,
  }: {
    transaction_counts_grouped_by_months: TransactionCountsGroupedByMonth[],
    transactions_price_spacious: number[],
    currency_unit: string | undefined,
  }): Promise<any> => {
  const currency_unit_with_fallback =
    (typeof currency_unit === "string") ?
      currency_unit :
      DataRetriever.get("currency-unit")

  /* eslint-disable no-undefined */
  return Promise.resolve(
    import(
      "highcharts"
    )
    .then((Highcharts) => {
      // Workaround for "Highcharts is not defined"
      window.Highcharts = Highcharts

      return new Highcharts.Chart($(element)[0] as string|HTMLElement, {
        chart: {
          marginLeft:   0,
          marginRight:  0,
          zoomType:     undefined,
        },
        credits: {
          enabled: false,
        },
        loading: {
          labelStyle: {
            color: "#ff8700",
          },
          style: {
            backgroundColor:  "white",
            opacity:          0.75,
          },
        },
        title: {
          text: "",
        },
        subtitle: {
          text: "",
        },
        xAxis: [
          {
            categories: transaction_counts_grouped_by_months.map(({first_day_of_the_month}) => {
              // dayjs only accepts lower cased locale
              return dayjs(first_day_of_the_month).add(2, "day").locale(I18n.locale.toLowerCase()).format("MMM YYYY")
            }),
            labels: {
              enabled:  true,
              style:    {
                fontSize: "13px",
              },
            },
            tickInterval: Page.isForMobile() ? 2 : 1,
          },
        ],
        yAxis: [
          {
            min:        0,
            // https://api.highcharts.com/highcharts/yAxis.max
            // Max value would be automatically calculated if set it as null.
            // Set it as 30000 if no data available
            max:        Math.max(...transactions_price_spacious) < 0 ? 30000 : null,
            // limit max number of yAxis to avoid unexpected overflow
            tickAmount: 4,
            labels:     {
              formatter: function(){
                return `${currency_unit_with_fallback}${this.value / 1000}k`
              },
              style:    {
                color:     "#1d3960",
                fontSize:   "13px",
              },
              // tickInterval: 1000
              align: "left" as "left",

              x: 3,
              y: 16,
            },
            title:          undefined,
            showFirstLabel: false,
          },
          {
            opposite: true,
            title:    undefined,

            labels: {
              format: "{value}",
              style:  {
                color:    "#c4c4c4",
                fontSize: "13px",
              },
              align: "right" as "right",

              x: -3,
              y: 16,
            },
            showFirstLabel: false,
          },
        ],
        tooltip: {
          shared: true,
        },
        plotOptions: {
          column: {
            states: {
              hover: {
                color: "#008ec7",
              },
            },
          },
        },
        legend: {
          enabled:  false,
          layout:   "vertical",
          align:    "left",

          verticalAlign: "top",

          x: 120,
          y: 100,

          floating: true,

          backgroundColor: "#FFFFFF",
        },
        series: [
          {
            name:     I18n.t("concepts.land.property.transaction.market_data_overview.highcharts.chart.data.transaction_volume"),
            type:     "column",
            color:    "#c4c4c4",
            yAxis:    1,
            data:     transaction_counts_grouped_by_months.map((e) => e.transaction_count),
            tooltip:  {
              valueSuffix: " " + I18n.t("concepts.land.property.transaction.market_data_overview.highcharts.chart.data.transactions"),
            },
          },
          {
            name:       I18n.t("concepts.land.property.transaction.market_data_overview.highcharts.chart.data.transaction_price"),
            type:       "spline",
            color:      "#1d3960",
            data:       transactions_price_spacious,
            lineWidth:  1.2,
            tooltip:    {
              valueSuffix: " " + I18n.t("concepts.land.property.transaction.market_data_overview.highcharts.chart.data.currency"),
            },
          },
        ],
      })
    }),
  )
  /* eslint-enable no-undefined */
}

const update_xaxis_tickInterval = (duration_selected: number) => {
  if (Page.isForMobile()){
    if (duration_selected === 6){
      return 2
    }
    else if (duration_selected === 12) {
      return 4
    }
    else if (duration_selected === 36) {
      return 11
    }
    else if (duration_selected === 60) {
      return 19
    }
    else {
      return 1
    }
  }
  else {
    if (duration_selected === 6) {
      return 1
    }
    else if (duration_selected === 12) {
      return 2
    }
    else if (duration_selected === 36) {
      return 4
    }
    else if (duration_selected === 60) {
      return 6
    }
    else {
      return 1
    }
  }
}

const update = (
  chart_promise: Promise<any>,
  {transaction_counts_grouped_by_months, transactions_price_spacious}: {
    transaction_counts_grouped_by_months: TransactionCountsGroupedByMonth[],
    transactions_price_spacious: number[],
  },
  duration_selected: number) => {
  const month_labels = transaction_counts_grouped_by_months.map(({first_day_of_the_month}) => {
    // dayjs only accepts lower cased locale
    return dayjs(first_day_of_the_month).add(2, "day").locale(I18n.locale.toLowerCase()).format("MMM YYYY")
  })

  // # Tempporary workaround to only show
  //   Transaction Volumn for all districts
  //   Will use static data (to be provided by server)
  chart_promise.then((chart) => {
    chart.series[0].setData(transaction_counts_grouped_by_months.map((e) => e.transaction_count))
    chart.series[1].setData(transactions_price_spacious)
    chart.xAxis[0].setCategories(month_labels)
    chart.xAxis[0].update({tickInterval: update_xaxis_tickInterval(duration_selected)})
    chart.hideLoading()
  })
}


export default {create, update}
